// Centralized SEO config for public pages. Social crawlers (Facebook/LinkedIn/X)
// do NOT execute JS, so the default OG/Twitter card lives in public/index.html.
// These per-route titles/descriptions + JSON-LD are for browser tabs and
// JS-rendering crawlers (Google). Keep copy compliance-safe: no claims of
// official/affiliated/guaranteed-pass.
import { FAQ, PRODUCT } from "@/data/content";

export const SITE_URL = (
  process.env.REACT_APP_SITE_URL || "https://illinoisbrokerprep.com"
).replace(/\/$/, "");

export const DEFAULT_TITLE =
  "Illinois Broker Exam Prep | Learn Illinois Law, Practice, and Simulate";
export const DEFAULT_DESCRIPTION =
  "Prepare for the Illinois real estate broker exam with Illinois-first lessons, source-backed practice, National and Illinois simulations, Question Coach help, and lifetime access. $79, one-time.";

// path -> { title, description, noindex }
export const ROUTE_META = {
  "/": {
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
  },
  "/product": {
    title: "Illinois Broker Prep Learning System | Source-Backed Study and Practice",
    description:
      "Learn Illinois broker exam topics, reinforce key terms, practice by topic, and take timed National and Illinois simulations with Question Coach help and lifetime access for $79.",
  },
  "/preview": {
    title: "Free Preview \u2014 Illinois Broker Exam Prep Sample Screens | Illinois Broker Prep",
    description:
      "Preview sample screens from the Illinois Broker Prep online study platform: dashboard, Illinois practice questions, explanations, score tracking, and timed review.",
  },
  "/about": {
    title: "About Illinois Broker Prep | A Coursewright Study Tool",
    description:
      "Learn how Illinois Broker Prep builds original, state-specific Illinois real estate broker exam practice questions and how Coursewright operates the study tool.",
  },
  "/coverage": {
    title: "Illinois Broker Exam Topic Coverage | Illinois Broker Prep",
    description:
      "See Illinois Broker Prep question counts, review dates, official PSI outline weights, and coverage across Illinois state law, national concepts, and real estate math.",
  },
  "/faq": {
    title: "FAQ \u2014 Illinois Broker Exam Prep | Illinois Broker Prep",
    description:
      "Answers about Illinois Broker Prep: what is included, whether questions are original, refunds, and how this Illinois broker license exam prep works.",
  },
  "/contact": {
    title: "Contact | Illinois Broker Prep",
    description:
      "Questions about the Illinois Broker Prep online study platform, your purchase, or a refund review? Contact our support team.",
  },
  "/live-review": {
    title: "Live Illinois Broker Exam Review | Illinois Broker Prep",
    description:
      "Join the Illinois Broker Prep priority list for upcoming small-group Illinois broker exam final reviews and private tutoring availability. No payment or reservation required.",
    noindex: true,
  },
  "/diagnostic": {
    title: "Free Illinois Broker Exam Diagnostic | Illinois Broker Prep",
    description:
      "Take a free 10-question Illinois real estate broker exam diagnostic. See your score, section breakdown, and weak areas right away — no signup to start.",
  },
  "/diagnostic/results": {
    title: "Your Diagnostic Results | Illinois Broker Prep",
    description:
      "Your free Illinois broker exam diagnostic results: score, section breakdown, weak areas, and a full question-by-question review.",
    noindex: true,
  },
  "/privacy": {
    title: "Privacy Policy | Illinois Broker Prep",
    description: "How Illinois Broker Prep collects, uses, and protects your information.",
  },
  "/terms": {
    title: "Terms of Service | Illinois Broker Prep",
    description: "The terms governing use of Illinois Broker Prep and the online study platform.",
  },
  "/refund": {
    title: "Refund Policy | Illinois Broker Prep",
    description:
      "Illinois Broker Prep refund policy: full online study access connects to your checkout email after purchase; contact us within 7 days for a satisfaction refund.",
  },
  "/disclaimer": {
    title: "Disclaimer | Illinois Broker Prep",
    description:
      "Illinois Broker Prep is an independent study aid. Not affiliated with PSI, IDFPR, the State of Illinois, or any real estate commission.",
  },
};

const ADMIN_PREFIXES = ["/settings", "/admin"];

export function metaForPath(pathname) {
  if (ADMIN_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/"))) {
    return { title: "Admin | Illinois Broker Prep", description: "", noindex: true };
  }
  return ROUTE_META[pathname] || { title: DEFAULT_TITLE, description: DEFAULT_DESCRIPTION };
}

// ---- JSON-LD builders (compliance-safe) ----
export function organizationJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Coursewright",
    alternateName: "Illinois Broker Prep",
    url: SITE_URL,
    logo: `${SITE_URL}/og-image.png`,
    description:
      "Coursewright operates Illinois Broker Prep, an independent Illinois real estate broker exam study tool with original practice questions, explanations, and timed review sprints. Not affiliated with PSI, IDFPR, the State of Illinois, or any real estate commission.",
    email: "support@illinoisbrokerprep.com",
  };
}

export function websiteJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Illinois Broker Prep",
    url: SITE_URL,
  };
}

export function productJsonLd(price = PRODUCT.price) {
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: "Illinois Broker Prep Online Study Platform",
    description:
      "Independent study aid for the Illinois real estate broker licensing exam: Illinois-first lessons, original practice questions, explanations, source references, timed exam-style practice, and readiness tracking. Current question-bank coverage is published on the Coverage page. Original practice material — no actual exam questions. Not affiliated with PSI, IDFPR, or the State of Illinois.",
    brand: { "@type": "Brand", name: "Illinois Broker Prep" },
    category: "Educational study material",
    url: `${SITE_URL}/product`,
    image: `${SITE_URL}/og-image.png`,
    offers: {
      "@type": "Offer",
      price: String(price || "79"),
      priceCurrency: "USD",
      availability: "https://schema.org/InStock",
      url: `${SITE_URL}/product`,
    },
  };
}

export function faqJsonLd(items = FAQ) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((it) => ({
      "@type": "Question",
      name: it.q,
      acceptedAnswer: { "@type": "Answer", text: it.a },
    })),
  };
}

export function jsonLdForPath(pathname, price) {
  if (pathname === "/") return [organizationJsonLd(), websiteJsonLd()];
  if (pathname === "/about") return [organizationJsonLd()];
  if (pathname === "/product") return [productJsonLd(price)];
  if (pathname === "/faq") return [faqJsonLd()];
  return [];
}
